import { userStore } from '@stores/User';

/**
 * Podcasts API — talks to the StarZero Agents (vibedit) podcast-projects
 * service. Two distinct env vars:
 *
 *  - `CHAT_API_URL` — the API host (e.g. `https://api.vibedit.ai`).
 *    All `/module/podcasts/*` HTTP calls go here. Shipped in
 *    vibedit#494 as a replacement for direct frontend calls to the
 *    workflow service.
 *  - `CHAT_URL` — the StarZero Agents web app origin
 *    (e.g. `https://chat.starzero.ai`). Used purely for deep-link URLs
 *    pointing at podcast project pages — never for HTTP calls.
 *
 * Response shapes here mirror the live `/instances` payload — only
 * fields the frontend actually consumes are typed strongly. Anything
 * the backend may add in future is `Record<string, unknown>` so we
 * don't break on unknown keys.
 */

const CHAT_URL = process.env.CHAT_URL?.replace(/\/$/, '') ?? '';
const CHAT_API_URL = process.env.CHAT_API_URL?.replace(/\/$/, '') ?? '';

const PODCASTS_API_BASE = CHAT_API_URL ? `${CHAT_API_URL}/module/podcasts` : '';

export type PodcastInstanceStatus =
  | 'queued'
  | 'pending'
  | 'completed'
  | 'failed'
  | 'partially-completed'
  | 'cancelled';

/** Setup payload the user picked when starting the podcast project. */
export interface PodcastInstanceSetup {
  libraryId?: string;
  selectedContentIds?: string[];
  title?: string;
  // Backend-extensible.
  [key: string]: unknown;
}

/** Render-time settings the user picked when configuring the project.
 *  Lives on every result-variables entry AND on `starterVariables` on
 *  the instance itself; the listing UI reads from `starterVariables`.
 *  Strings are open-ended unions to stay forward-compatible with new
 *  presets the backend may introduce. */
export type PodcastDurationBin = '15s-1min' | '1-2min' | '2-3min' | 'mix';
export type PodcastPacing = 'original' | 'medium' | 'fast';
export type PodcastReframing = 'speaker' | 'intercut';

export interface PodcastInstanceVariables {
  // Configuration the user picked.
  clip_count?: number;
  duration?: PodcastDurationBin;
  pacing?: PodcastPacing;
  reframing?: PodcastReframing;
  aspect_ratio_num?: number;
  aspect_ratio_den?: number;
  captions_enabled?: boolean;
  add_music?: boolean;
  correct_transcript?: boolean;
  user_notes?: string;
  // Caption presets — see `CaptionPresets.ts` in the vibedit repo.
  caption_style?: string;
  caption_text_color?: string;
  caption_highlight_color?: string;
  caption_outline_color?: string;
  caption_outline_boldness?: number;
  caption_shadow?: boolean;
  // Per-result bookkeeping (only present on entries inside `results.variables`,
  // not on `starterVariables`).
  index?: number;
  cardinality?: number[];
  session_id?: string;
  planned_clip_count?: number;
  candidate_count?: number;
  // Backend-extensible.
  [key: string]: unknown;
}

export interface PodcastInstanceAnalysis {
  title?: string;
  text?: string;
  session_id?: string;
  [key: string]: unknown;
}

export interface PodcastInstanceResults {
  analysis?: PodcastInstanceAnalysis[];
  variables?: PodcastInstanceVariables[];
  [key: string]: unknown;
}

/** A single podcast project. Mirrors the backend `WorkflowInstance` /
 *  `Podcast` document. Per CLAUDE.md's "do not trust backend types"
 *  rule, every field except `_id` is optional — fields may be absent
 *  depending on the project's lifecycle stage (queued → completed),
 *  and the backend may add or rename fields without ceremony.
 *  Consumers must optional-chain. `_id` itself stays required: a
 *  document without an identifier shouldn't appear in the response. */
export interface PodcastInstance {
  _id: string;
  /** Same value as `_id`. Present for callers that prefer `id`. */
  id?: string;
  owner?: string;
  name?: string;
  workflowTemplateId?: string;
  isDone?: boolean;
  status?: PodcastInstanceStatus;
  setup?: PodcastInstanceSetup;
  expectedOutputCount?: number;
  results?: PodcastInstanceResults;
  createdAt?: number;
  updatedAt?: number;
  /** The configuration variables the user picked at creation. The
   *  listing UI reads `clip_count`, `duration`, `pacing`, `reframing`,
   *  etc. from here. */
  starterVariables?: PodcastInstanceVariables;
  creditsUsed?: number;
}

export interface ListPodcastInstancesOptions {
  /** Page size. Defaults to 25. The server reads `_start`/`_end` as a
   *  half-open zero-indexed range. */
  pageSize?: number;
  /** Zero-indexed offset of the first item. Defaults to 0. */
  start?: number;
  /** Field to sort by. Defaults to `createdAt`. */
  sortBy?: 'createdAt' | 'updatedAt' | 'name';
  /** Sort direction. Defaults to `desc` (newest first). */
  sortOrder?: 'asc' | 'desc';
  /** Allows the caller to abort the in-flight request — useful inside
   *  `useEffect` cleanups. */
  signal?: AbortSignal;
}

/**
 * GET ${CHAT_API_URL}/module/podcasts/instances
 *
 * Returns the user's podcast projects, newest first by default.
 * Returns an empty array if `CHAT_API_URL` is not configured or there
 * is no auth token (i.e. logged-out callers fall through silently
 * rather than throwing — matches how the rest of the codebase treats
 * unauth'd background fetches).
 */
export async function listPodcastInstances(
  options: ListPodcastInstancesOptions = {},
): Promise<PodcastInstance[]> {
  if (!PODCASTS_API_BASE) {
    console.warn('CHAT_API_URL not set — skipping podcast projects fetch.');
    return [];
  }
  const token = userStore.token;
  if (!token) return [];

  const pageSize = options.pageSize ?? 25;
  const start = options.start ?? 0;
  const sortBy = options.sortBy ?? 'createdAt';
  const sortOrder = options.sortOrder ?? 'desc';

  const params = new URLSearchParams({
    _sort: sortBy,
    _order: sortOrder,
    _start: String(start),
    _end: String(start + pageSize),
  });

  const res = await fetch(`${PODCASTS_API_BASE}/instances?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: options.signal,
  });
  if (!res.ok) {
    throw new Error(`Podcasts API ${res.status}`);
  }
  return (await res.json()) as PodcastInstance[];
}

/**
 * Build a deep-link URL to a podcast project's page on the chat web
 * app. Returns an empty string when `CHAT_URL` isn't configured so
 * callers can disable the link cleanly.
 */
export function getPodcastProjectUrl(instanceId: string): string {
  if (!CHAT_URL) return '';
  return `${CHAT_URL}/podcasts/${instanceId}`;
}

/**
 * Build the URL to the create-podcast-project page.
 *
 * Pass both `libraryId` and `mediaId` to pre-seed the flow with a specific
 * media — the agents app serves `/podcasts/create/:libraryId/:mediaId` for
 * exactly that. Omit them for the generic create screen, which is what the
 * Workspace hero and the Podcasts page use.
 *
 * The media-scoped route needs BOTH ids, so a half-supplied pair deliberately
 * falls back to the generic screen rather than building `/create/undefined`.
 * That keeps the caller honest without making every call site defensive.
 */
export function getPodcastCreateUrl(libraryId?: string, mediaId?: string): string {
  if (!CHAT_URL) return '';
  if (libraryId && mediaId) return `${CHAT_URL}/podcasts/create/${libraryId}/${mediaId}`;
  return `${CHAT_URL}/podcasts/create`;
}

/**
 * Build the URL to the all-podcast-projects landing page.
 */
export function getPodcastsLandingUrl(): string {
  if (!CHAT_URL) return '';
  return `${CHAT_URL}/podcasts`;
}
